import { Fragment, useEffect, useRef, useState } from "react";
import { GoogleMap, MarkerF, CircleF, InfoWindowF, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "@/lib/googleMaps";
import { fmtHora as fmtHoraTz } from "@/lib/datetime";

const TIPO_LABEL = {
  entrada: "Entrada",
  inicio_descanso: "Inicio de descanso",
  fin_descanso: "Fin de descanso",
  salida: "Salida",
};

const TIPO_COLOR = {
  entrada: "#059669",
  inicio_descanso: "#d97706",
  fin_descanso: "#d97706",
  salida: "#1e293b",
};

const MAP_OPTIONS = { scrollwheel: false, streetViewControl: false, mapTypeControl: false, fullscreenControl: false };
const BOUNDS_PADDING = 40;

function fmtHora(iso, tz) {
  const s = fmtHoraTz(iso, tz);
  return s === "—" ? "" : s;
}

/**
 * Muestra dónde se capturó cada evento de check-in del día (entrada, descansos,
 * salida) relativo a la geocerca del centro de trabajo. Acepta `eventos`
 * (arreglo de geo_eventos del registro); si no viene o está vacío, cae al
 * punto único legado (lat/lng/distanciaMetros/dentroGeocerca) para registros
 * guardados antes de que existiera geo_eventos.
 */
export default function CheckinMap({ eventos, lat, lng, distanciaMetros, dentroGeocerca, centro, height = 260 }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const mapRef = useRef(null);
  // Índice del punto con InfoWindow abierta, "centro" para la geocerca, o null.
  const [activo, setActivo] = useState(null);

  const puntos = eventos && eventos.length > 0
    ? eventos.filter((e) => e.lat != null && e.lng != null)
    : (lat != null && lng != null
        ? [{ tipo: null, fecha: null, lat, lng, distancia_metros: distanciaMetros, dentro_geocerca: dentroGeocerca }]
        : []);
  const hasCentro = centro?.geo_lat != null && centro?.geo_lng != null;
  const tiposPresentes = [...new Set(puntos.map((p) => p.tipo).filter(Boolean))];

  // Encuadra todos los puntos + la geocerca cada vez que cambian, en vez de un
  // center/zoom fijo — así siempre se ve todo lo relevante del día sin recortar nada.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || puntos.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    puntos.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (hasCentro) bounds.extend({ lat: centro.geo_lat, lng: centro.geo_lng });
    mapRef.current.fitBounds(bounds, BOUNDS_PADDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, JSON.stringify(puntos), hasCentro]);

  if (puntos.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-md"
        style={{ height }}
        data-testid="checkin-map-empty"
      >
        Sin ubicación registrada para este día.
      </div>
    );
  }
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div
        className="flex items-center justify-center text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-md"
        style={{ height }}
      >
        El mapa no está disponible: falta configurar la API de Google Maps.
      </div>
    );
  }
  if (loadError) {
    return (
      <div
        className="flex items-center justify-center text-sm text-red-600 bg-red-50 border border-dashed border-red-300 rounded-md"
        style={{ height }}
      >
        No se pudo cargar Google Maps. Revisa tu conexión e intenta de nuevo.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div
        className="flex items-center justify-center text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-md"
        style={{ height }}
      >
        Cargando mapa…
      </div>
    );
  }

  return (
    <div data-testid="checkin-map">
      <div className="rounded-md overflow-hidden border border-slate-200">
        <GoogleMap
          mapContainerStyle={{ height, width: "100%" }}
          center={{ lat: puntos[0].lat, lng: puntos[0].lng }}
          zoom={16}
          options={MAP_OPTIONS}
          onLoad={(map) => { mapRef.current = map; }}
        >
          {hasCentro && (
            <>
              <CircleF
                center={{ lat: centro.geo_lat, lng: centro.geo_lng }}
                radius={centro.geo_radio_metros || 200}
                options={{ strokeColor: "#0f172a", strokeWeight: 1, fillColor: "#0f172a", fillOpacity: 0.06, clickable: true }}
                onClick={() => setActivo("centro")}
              />
              {activo === "centro" && (
                <InfoWindowF position={{ lat: centro.geo_lat, lng: centro.geo_lng }} onCloseClick={() => setActivo(null)}>
                  <span>Geocerca de {centro.nombre || "este centro"} ({centro.geo_radio_metros || 200} m)</span>
                </InfoWindowF>
              )}
            </>
          )}
          {puntos.map((p, i) => {
            const fuera = p.dentro_geocerca === false;
            const color = TIPO_COLOR[p.tipo] || "#334155";
            return (
              <Fragment key={i}>
                <MarkerF
                  position={{ lat: p.lat, lng: p.lng }}
                  onClick={() => setActivo(i)}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 9,
                    fillColor: color,
                    fillOpacity: 0.9,
                    strokeColor: fuera ? "#dc2626" : "#ffffff",
                    strokeWeight: fuera ? 3 : 2,
                  }}
                />
                {activo === i && (
                  <InfoWindowF position={{ lat: p.lat, lng: p.lng }} onCloseClick={() => setActivo(null)}>
                    <div>
                      <strong>{TIPO_LABEL[p.tipo] || "Check-in"}</strong>
                      {p.fecha && <div>{fmtHora(p.fecha, centro?.zona_horaria)}</div>}
                      {p.distancia_metros != null && (
                        <div>
                          {Math.round(p.distancia_metros)} m del centro
                          {fuera ? " · fuera de la geocerca" : p.dentro_geocerca === true ? " · dentro de la geocerca" : ""}
                        </div>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </Fragment>
            );
          })}
        </GoogleMap>
      </div>
      {tiposPresentes.length > 1 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500">
          {tiposPresentes.map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: TIPO_COLOR[t] || "#334155" }} />
              {TIPO_LABEL[t] || t}
            </span>
          ))}
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block border-2 border-red-600" />
            fuera de geocerca
          </span>
        </div>
      )}
    </div>
  );
}
